import { CandidateProfileIssuesUtils } from '@axiom/utils';
import type {
  CandidateProfileIssue,
  Experience,
  Education,
} from '@axiom/validation';
import { ProfileCompletionConst } from '@axiom/const';

import { ExperienceUtil } from './experience-util';

const { ProfileCompletionCriteria } = ProfileCompletionConst;

export type ActionNeededItem = {
  name: string;
  prependText: string;
  linkText?: string;
  linkTo?: string;
};

export const ProfileCompletionUtil = {
  getActionNeededItems: (
    issues: CandidateProfileIssue[] = [],
    experiences: Experience[] = [],
    educations?: Education[] | null
  ): ActionNeededItem[] => {
    const actionNeededItems = [];
    educations = educations || [];

    if (CandidateProfileIssuesUtils.hasAvailabilityIssues(issues)) {
      actionNeededItems.push({
        name: ProfileCompletionCriteria.needsConfirmedAvailability,
        prependText: 'Confirm your preferred weekly hours',
      });
    }

    if (CandidateProfileIssuesUtils.hasValidNameIssues(issues)) {
      actionNeededItems.push({
        name: ProfileCompletionCriteria.needsValidName,
        prependText: 'Update your display name',
      });
    }

    if (CandidateProfileIssuesUtils.hasSummaryOfAnyLengthIssues(issues)) {
      actionNeededItems.push({
        name: ProfileCompletionCriteria.needsAcceptableSummary,
        prependText: 'Add a',
        linkText: 'profile summary',
        linkTo: '?modal=profileSummary',
      });
    }

    if (
      CandidateProfileIssuesUtils.hasSummaryOfSufficientLengthIssues(issues)
    ) {
      actionNeededItems.push({
        name: ProfileCompletionCriteria.needsAcceptableSummary,
        prependText: 'Expand on your',
        linkText: 'profile summary',
        linkTo: '?modal=profileSummary',
      });
    }

    if (CandidateProfileIssuesUtils.hasExperienceSpecificIssues(issues)) {
      const orderedExperiences =
        ExperienceUtil.orderExperiencesForDisplay(experiences);

      const experienceIssues = CandidateProfileIssuesUtils.getExperienceIssues(
        issues,
        experiences
      ).sort(
        (a, b) =>
          orderedExperiences.findIndex(o => o.id === a.id) -
          orderedExperiences.findIndex(o => o.id === b.id)
      );

      actionNeededItems.push(
        ...experienceIssues.map(e => ({
          name: `${ProfileCompletionCriteria.needsExperienceUpdate}_${e.id}`,
          ...(e.calculatedDisplayName
            ? {
                prependText: 'Expand on your experience at',
                linkText: e.calculatedDisplayName,
              }
            : {
                prependText: 'Expand on your experience with',
                linkText: 'missing details',
              }),
          linkTo: `../experiences/${e.id}`,
        }))
      );
    }

    if (CandidateProfileIssuesUtils.hasNonAxiomExperienceIssues(issues)) {
      actionNeededItems.push({
        name: ProfileCompletionCriteria.needsNonAxiomExperience,
        prependText: 'Add a',
        linkText: 'non-Axiom experience',
        linkTo: '../experiences/new',
      });
    }

    if (CandidateProfileIssuesUtils.hasLegalTechSkillsIssues(issues)) {
      actionNeededItems.push({
        name: ProfileCompletionCriteria.needsLegalTechSkills,
        prependText: 'Add',
        linkText: 'legal tech skills',
        linkTo: '?modal=legalTechSkills',
      });
    }

    if (CandidateProfileIssuesUtils.hasMissingEducationIssue(issues)) {
      actionNeededItems.push({
        name: ProfileCompletionCriteria.needsEducation,
        prependText: 'Complete your',
        linkText: 'education section',
        linkTo: '?modal=education',
      });
    }

    if (CandidateProfileIssuesUtils.hasEducationSpecificIssues(issues)) {
      const educationIssues =
        CandidateProfileIssuesUtils.getEducationSpecificIssues(
          issues,
          educations
        );

      actionNeededItems.push(
        ...educationIssues.map(e => ({
          name: `${ProfileCompletionCriteria.needsEducationUpdate}_${e.id}`,
          ...(e.institution
            ? {
                prependText: 'Complete your education section for',
                linkText: e.institution,
              }
            : {
                prependText: 'Complete your education section with',
                linkText: 'missing details',
              }),
          linkTo: `?modal=education&id=${e.id}`,
        }))
      );
    }

    if (CandidateProfileIssuesUtils.hasNativeLanguageIssues(issues)) {
      actionNeededItems.push({
        name: ProfileCompletionCriteria.needsNativeLanguage,
        prependText: 'Add a',
        linkText: 'language',
        linkTo: '?modal=language',
      });
    }

    if (CandidateProfileIssuesUtils.hasPreferencesIssues(issues)) {
      const [issue] = CandidateProfileIssuesUtils.getPreferencesIssues(issues);
      if (issue) {
        actionNeededItems.push({
          name: ProfileCompletionCriteria.needsUpdatedPreferences,
          prependText: 'Review and update your',
          linkText: 'preferences',
          linkTo: `../preferences/${issue.context}`,
        });
      }
    }

    return actionNeededItems;
  },
};
